// A row's selection cell, in the reserved selection column; the summary row passes no row and gets a blank
// cell. The checkbox names its row. An ineligible row's checkbox stays focusable, reads aria-disabled and
// carries its reason; under query-scoped selection, and while bulk work runs, every checkbox keeps its
// checked state and rejects changes. Shift-click selects the eligible rows between the last-clicked row and
// this one.

import React, { useId } from 'react'
import PropTypes from 'prop-types'

import { useTable } from '../context'
import { displayText } from '../format'
import { rowLabel } from './copy'
import { ineligibleReason, rowId } from './selection'
import { useSelection } from './useSelection'

const CELL = 'me-table-control me-table-select'
const noop = () => {}
// a click in the cell is a selection gesture, never a row-body click that expands the row
const contain = event => event.stopPropagation()

export const SelectCell = ({ row }) => {
  if (!row) return <td className={CELL} />
  return <RowCheckbox row={row} />
}

SelectCell.propTypes = {
  row: PropTypes.object,
}

const RowCheckbox = ({ row }) => {
  const { config, layout, derived } = useTable()
  const selection = useSelection()
  const reasonId = useId()
  const id = rowId(config, row)
  const eligible = selection.isEligible(row)
  const reason = eligible ? null : ineligibleReason(config, row)
  const locked =
    !eligible || selection.queryScoped || Boolean(selection.pending)
  const identity = layout.idColumn ? displayText(layout.idColumn, row) : id

  const onClick = event => {
    // a locked checkbox keeps its state: the controlled value restores it after the click
    if (locked) return
    const index = (derived.pageRows || []).findIndex(
      other => rowId(config, other) === id
    )
    selection.toggleRow(row, index, event.shiftKey)
  }

  return (
    <td className={CELL} onClick={contain}>
      <input
        type="checkbox"
        checked={selection.isSelected(id, row)}
        onChange={noop}
        onClick={onClick}
        aria-label={rowLabel(identity)}
        aria-disabled={locked || undefined}
        aria-describedby={reason ? reasonId : undefined}
        data-ineligible={eligible ? undefined : ''}
        data-tooltip={reason ? rowLabel(identity) : undefined}
        data-tooltip-explanation={reason || undefined}
      />
      {reason && (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      )}
    </td>
  )
}

RowCheckbox.propTypes = {
  row: PropTypes.object.isRequired,
}
